import type { LLMInstance } from '@/types';
import { LLM_PARAMS } from '@/lib/constants';

// 构建代理 URL（解决浏览器 CORS 限制）
function proxyUrl(target: string): string {
    return `/api/proxy?target=${encodeURIComponent(target)}`;
}

// 构建 /chat/completions 真实目标地址
// 兼容两种 base url 写法：
//   - https://api.deepseek.com            -> .../v1/chat/completions
//   - .../compatible-mode/v1              -> .../v1/chat/completions
function buildTargetUrl(instance: LLMInstance): string {
    const base = instance.apiUrl.replace(/\/+$/, '');
    return base.endsWith('/v1')
        ? `${base}/chat/completions`
        : `${base}/v1/chat/completions`;
}

// 经过代理转发的请求地址（实际发起请求用）
function buildChatUrl(instance: LLMInstance): string {
    return proxyUrl(buildTargetUrl(instance));
}

type ChatMessage = {
    role: 'system' | 'user' | 'assistant';
    content: string | Array<Record<string, unknown>>;
};

// 根据提示词与图片构建 messages
function buildMessages(
    systemPrompt: string,
    userText: string,
    imageUrls: string[],
): ChatMessage[] {
    if (imageUrls.length > 0) {
        const content: Array<Record<string, unknown>> = imageUrls.map((url) => ({
            type: 'image_url',
            image_url: { url },
        }));
        content.push({ type: 'text', text: userText });
        return [
            { role: 'system', content: systemPrompt },
            { role: 'user', content },
        ];
    }
    return [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userText },
    ];
}

// 生成等效的 curl 命令（用于运行日志展示实际请求内容）
export function buildLLMCurlCommand(
    instance: LLMInstance,
    model: string,
    systemPrompt: string,
    userText: string,
    imageUrls: string[] = [],
): string {
    const url = buildTargetUrl(instance);
    const messages = buildMessages(systemPrompt, userText, imageUrls);
    const body = {
        model,
        messages,
        temperature: LLM_PARAMS.TEMPERATURE,
        max_tokens: LLM_PARAMS.MAX_TOKENS,
    };
    const json = JSON.stringify(body, null, 2).replace(/'/g, "'\\''");
    return [
        `curl -X POST '${url}' \\`,
        `  -H 'Content-Type: application/json' \\`,
        `  -H 'Authorization: Bearer ${instance.apiKey}' \\`,
        `  -d '${json}'`,
    ].join('\n');
}

// 发送对话请求并解析返回文本
async function postChat(
    instance: LLMInstance,
    model: string,
    messages: ChatMessage[],
): Promise<string> {
    const url = buildChatUrl(instance);

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${instance.apiKey}`,
        },
        body: JSON.stringify({
            model,
            messages,
            temperature: LLM_PARAMS.TEMPERATURE,
            max_tokens: LLM_PARAMS.MAX_TOKENS,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`LLM 请求失败 (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    const content = data?.choices?.[0]?.message?.content;
    if (!content) {
        throw new Error('LLM 返回内容为空');
    }
    return String(content).trim();
}

// 调用 OpenAI 兼容的 /chat/completions 接口（纯文本）
export async function callLLM(
    instance: LLMInstance,
    model: string,
    systemPrompt: string,
    userMessage: string,
): Promise<string> {
    return postChat(instance, model, buildMessages(systemPrompt, userMessage, []));
}

export async function fetchLLMModels(instance: LLMInstance): Promise<string[]> {
    const base = instance.apiUrl.trim().replace(/\/+$/, '');
    const target = `${base.endsWith('/v1') ? base : `${base}/v1`}/models`;
    const response = await fetch(proxyUrl(target), {
        headers: { Accept: 'application/json', Authorization: `Bearer ${instance.apiKey}` },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(String(data?.error?.message ?? data?.message ?? `读取模型失败 (${response.status})`));
    const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data?.models) ? data.models : [];
    return Array.from(new Set(rows.map((row: unknown) => typeof row === 'string' ? row : String((row as { id?: unknown })?.id ?? '')).filter(Boolean)));
}

// 调用 OpenAI 兼容的多模态接口（文本 + 图片）
// imageUrls 支持 http(s) 图片链接或 data: 开头的 base64 图片
export async function callLLMWithImages(
    instance: LLMInstance,
    model: string,
    systemPrompt: string,
    userText: string,
    imageUrls: string[],
): Promise<string> {
    const validImages = imageUrls.map((img) => (img ?? '').trim()).filter(Boolean);
    return postChat(instance, model, buildMessages(systemPrompt, userText, validImages));
}

// 优化标题
export async function optimizeTitle(
    instance: LLMInstance,
    model: string,
    systemPrompt: string,
    title: string,
): Promise<string> {
    const optimized = await callLLM(instance, model, systemPrompt, title);
    return optimized;
}
